import { useAssetVizInternal } from '../AssetVizInternalProvider'
import {
  BoundsCalculator,
  DefaultCamera,
  FitCalculator,
  PositionCalculator,
  SelectionCallback,
} from '../cameras/DefaultCamera'
import { getConeCameraPosition } from '../cameras/defaultCameraUtils'
import { ConeSurface3D } from '../objects/surfaces/ConeSurface3D'
import { FlatRoofSurface3D } from '../objects/surfaces/FlatRoofSurface3D'
import { FloorSurface3D } from '../objects/surfaces/FloorSurface3D'
import { FoundationSurface3D } from '../objects/surfaces/FoundationSurface3D'
import { LegacyConeSurface3D } from '../objects/surfaces/LegacyConeSurface3D'
import { ShellSurface3D } from '../objects/surfaces/ShellSurface3D'
import { SceneFactoryConfig } from './sceneFactory'
import React from 'react'
import {
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoCone,
  GeckoEditableComponent,
  GeckoLegacyCone,
  GeckoMeasurementUnit,
  GeckoTankFlatRoof,
  GeckoTankFloor,
  GeckoVerticalTankFoundation,
  GeckoVerticalTankShell,
} from 'types'

export const VerticalTankSceneConfig: SceneFactoryConfig = {
  name: 'VerticalTank',
  assetModels: [GeckoAssetModelType.VerticalTank],
  componentModels: [
    GeckoComponentModelType.VerticalTankShell,
    GeckoComponentModelType.TankFlatRoof,
    GeckoComponentModelType.TankFloor,
    GeckoComponentModelType.Cone,
    GeckoComponentModelType.VerticalTankFoundation,
    GeckoComponentModelType.LegacyCone,
  ],
  sceneFunc: (props) => <VerticalTankScene {...props} />,
}

/**
 * A scene depicting a cylindrical tank (not a military tank, mind you) which
 * wraps around the y-axis.
 * @param assetModel      The model used to represent the whole asset
 * @param componentModel  The model for the component in question
 */
export const VerticalTankScene: React.FC<{
  assetModel: GeckoAssetModel
  componentModel: GeckoEditableComponent
}> = ({ assetModel, componentModel }) => {
  const selectedModel = componentModel as
    | GeckoVerticalTankShell
    | GeckoTankFlatRoof
    | GeckoTankFloor
    | GeckoCone
    | GeckoLegacyCone
    | GeckoVerticalTankFoundation

  //----------------------------------------------------------------------------
  const { show3D, componentDataFrames } = useAssetVizInternal()

  const radiusConeCase = React.useMemo(
    () =>
      selectedModel.type === GeckoComponentModelType.LegacyCone ||
      (!show3D && selectedModel.type === GeckoComponentModelType.Cone),
    [show3D, selectedModel.type]
  )

  const componentBounds = React.useMemo(() => {
    if (radiusConeCase)
      return GeckoComponentRect.CreateFromXYs(
        selectedModel.componentBounds.componentID,
        -selectedModel.componentBounds.end.y.num,
        -selectedModel.componentBounds.end.y.num,
        selectedModel.componentBounds.end.y.num,
        selectedModel.componentBounds.end.y.num,
        selectedModel.componentBounds.y.unit,
        selectedModel.componentBounds.y.unit
      )
    const componentBounds = selectedModel.componentBounds
    if (
      componentBounds.y.unit !== GeckoMeasurementUnit.Meters ||
      (selectedModel.type !== GeckoComponentModelType.Cone &&
        componentBounds.x.unit !== GeckoMeasurementUnit.Meters) ||
      (selectedModel.type === GeckoComponentModelType.Cone &&
        componentBounds.x.unit !== GeckoMeasurementUnit.Degrees)
    )
      throw new Error(
        `X and y measurement units should also be in meters 
        (unless its a Cone component, then x should be in degrees), but
              the x range is in ${componentBounds.x.unit.id}
              and y range is in ${componentBounds.y.unit.id}
              and the selected component type is ${selectedModel.type} `
      )
    return componentBounds
  }, [selectedModel.componentBounds, radiusConeCase, selectedModel.type])

  const tankRadius = React.useMemo(
    () =>
      selectedModel instanceof GeckoLegacyCone ||
      selectedModel instanceof GeckoCone
        ? selectedModel.outerRadius
        : selectedModel.radius,
    [selectedModel]
  )

  const onComponentSelected = React.useCallback<SelectionCallback>(
    () =>
      // This starts the camera off wherever there's data to look at
      radiusConeCase
        ? componentBounds
        : (componentDataFrames
            ?.at(0)
            ?.data?.geometry?.project(componentBounds.space) ??
          componentBounds),
    [componentDataFrames, componentBounds, radiusConeCase]
  )

  const getCameraBounds = React.useCallback<BoundsCalculator>(
    () => ({
      rect: componentBounds,
      doWrapX:
        show3D &&
        (selectedModel.type === GeckoComponentModelType.VerticalTankShell ||
          selectedModel.type === GeckoComponentModelType.Cone),
    }),
    [componentBounds, show3D, selectedModel.type]
  )

  const getCameraPosition = React.useCallback<PositionCalculator>(
    ({ componentX, componentY, distToComponent }) => {
      const relX = componentX - componentBounds.start.x.num
      const relY = componentY - componentBounds.start.y.num
      if (
        selectedModel.type === GeckoComponentModelType.TankFlatRoof ||
        selectedModel.type === GeckoComponentModelType.TankFloor
      )
        return {
          position: [relX, relY, distToComponent],
          rotation: [0, 0, 0],
        }
      else if (selectedModel.type === GeckoComponentModelType.LegacyCone)
        return {
          position: [componentX, componentY, distToComponent],
          rotation: [0, 0, 0],
        }
      else if (selectedModel.type === GeckoComponentModelType.Cone) {
        if (!show3D)
          return {
            position: [componentX, componentY, distToComponent],
            rotation: [0, 0, 0],
          }
        return getConeCameraPosition(
          componentX,
          componentY,
          distToComponent,
          selectedModel,
          onComponentSelected(selectedModel.id) ?? componentBounds
        )
      }

      if (!show3D)
        return {
          position: [relX, relY, distToComponent],
          rotation: [0, 0, 0],
        }
      // ...otherwise we're looking at the shell in 3D mode
      const angle =
        2.0 * Math.PI * (relX / (tankRadius.num * 2 * Math.PI) - 0.25)

      const distToAxis = tankRadius.num + distToComponent
      return {
        position: [
          Math.sin(angle) * distToAxis,
          relY,
          Math.cos(angle) * distToAxis,
        ],
        rotation: [0, angle, 0],
      }
    },
    [
      componentBounds,
      tankRadius.num,
      show3D,
      selectedModel,
      onComponentSelected,
    ]
  )

  const cameraKey = React.useMemo(
    () =>
      selectedModel instanceof GeckoCone ? (show3D ? 'cone3D' : 'cone2D') : '',
    [selectedModel, show3D]
  )

  const getCameraDimensionsFit = React.useCallback<FitCalculator>(
    (rect) =>
      show3D && selectedModel.type === GeckoComponentModelType.VerticalTankShell
        ? [Math.min(rect.width.num, tankRadius.num * 2.0), rect.height.num]
        : show3D && selectedModel.type === GeckoComponentModelType.Cone
          ? [tankRadius.num * 2.0, selectedModel.coneHeight.num]
          : [rect.width.num, rect.height.num],
    [show3D, tankRadius, selectedModel]
  )

  //----------------------------------------------------------------------------

  return !selectedModel ? null : (
    <>
      <DefaultCamera
        key={cameraKey}
        onComponentSelected={onComponentSelected}
        getCameraBounds={getCameraBounds}
        getCameraPosition={getCameraPosition}
        getCameraDimensionsFit={getCameraDimensionsFit}
      />
      {selectedModel instanceof GeckoVerticalTankShell ? (
        <ShellSurface3D model={selectedModel} assetModel={assetModel} />
      ) : selectedModel instanceof GeckoTankFloor ? (
        <FloorSurface3D
          model={selectedModel}
          position={[tankRadius.num, tankRadius.num, 0]}
          assetModel={assetModel}
        />
      ) : selectedModel instanceof GeckoTankFlatRoof ? (
        <FlatRoofSurface3D
          model={selectedModel}
          position={[tankRadius.num, tankRadius.num, 0]}
          assetModel={assetModel}
        />
      ) : selectedModel instanceof GeckoLegacyCone ? (
        <LegacyConeSurface3D model={selectedModel} assetModel={assetModel} />
      ) : selectedModel instanceof GeckoCone ? (
        <ConeSurface3D model={selectedModel} assetModel={assetModel} />
      ) : selectedModel instanceof GeckoVerticalTankFoundation ? (
        <FoundationSurface3D model={selectedModel} assetModel={assetModel} />
      ) : null}
    </>
  )
}
